import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { customerApi, formatApiErrorDetail } from "../lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const PAYPAL_CID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, total, clear } = useCart();
  const { customer } = useCustomer();
  const [step, setStep] = useState("cart"); // cart | checkout | done
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", address: "", note: "" });
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [codeMsg, setCodeMsg] = useState("");
  const [shipCfg, setShipCfg] = useState({ flat_rate: 8, free_threshold: 50 });

  useEffect(() => {
    customerApi.get("/shipping/config").then((r) => setShipCfg(r.data)).catch(() => {});
  }, []);

  const discount = appliedCode ? +(total * discountPct / 100).toFixed(2) : 0;
  const merchandise = +(total - discount).toFixed(2);
  const freeShip = merchandise >= shipCfg.free_threshold;
  const shipping = merchandise > 0 && !freeShip ? shipCfg.flat_rate : 0;
  const payable = +(merchandise + shipping).toFixed(2);
  const awayFromFree = +(shipCfg.free_threshold - merchandise).toFixed(2);

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    try {
      const { data } = await customerApi.post("/ambassador/validate", { code });
      if (data.valid) {
        setAppliedCode(data.code);
        setDiscountPct(data.discount_pct);
        setCodeMsg(`Code applied — ${data.discount_pct}% off`);
        toast.success(`${data.discount_pct}% off applied`);
      } else {
        setAppliedCode("");
        setDiscountPct(0);
        setCodeMsg("That code isn't valid or is inactive");
      }
    } catch {
      setCodeMsg("Could not check that code");
    }
  };

  const autoTried = useRef(false);
  const removeCode = () => {
    setAppliedCode("");
    setDiscountPct(0);
    setCodeInput("");
    setCodeMsg("");
    localStorage.removeItem("drift_ref"); // user opted out — don't re-apply
    autoTried.current = true;
  };

  // Auto-apply a referral code (from ?ref=CODE) ONCE per drawer session, and never after the user removes it
  useEffect(() => {
    if (!open) {
      autoTried.current = false; // reset for the next time the drawer opens
      return;
    }
    if (autoTried.current || appliedCode) return;
    const ref = localStorage.getItem("drift_ref");
    if (!ref) return;
    autoTried.current = true;
    (async () => {
      try {
        const { data } = await customerApi.post("/ambassador/validate", { code: ref });
        if (data.valid) {
          setAppliedCode(data.code);
          setDiscountPct(data.discount_pct);
          setCodeMsg(`Referral ${data.code} applied — ${data.discount_pct}% off`);
        } else {
          localStorage.removeItem("drift_ref"); // stale/invalid code, forget it
        }
      } catch { /* ignore */ }
    })();
  }, [open, appliedCode]);

  // Prefill name/email for a logged-in angler (only fills blanks, never overwrites typing)
  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.name || "",
        email: f.email || customer.email || "",
      }));
    }
  }, [customer]);

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep("cart"), 400);
  };

  const orderItems = () =>
    items.map((i) => ({
      product_id: i.product_id, name: i.name, price: i.price, color: i.color, quantity: i.quantity,
      custom_specs: i.custom_specs || null,
    }));

  const createPaypalOrder = async () => {
    const { data } = await customerApi.post("/paypal/create-order", { ...form, items: orderItems(), total: payable, ambassador_code: appliedCode || null });
    return data.id;
  };

  const onPaypalApprove = async (data) => {
    try {
      await customerApi.post("/paypal/capture-order", { paypal_order_id: data.orderID });
      clear();
      removeCode();
      setStep("done");
    } catch (err) {
      toast.error("Payment could not be completed");
    }
  };

  const payWithCard = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.address) {
      toast.error("Add your name, email and shipping address first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await customerApi.post("/checkout/stripe-session", {
        ...form,
        items: orderItems(),
        total: payable,
        ambassador_code: appliedCode || null,
        origin_url: window.location.origin,
      });
      // cart clears on the success page once payment is confirmed
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[201] w-full max-w-md bg-[#04070A]/95 backdrop-blur-2xl border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-white/5">
              <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-slate-400">
                {step === "cart" && "Your Cart"}
                {step === "checkout" && "Checkout"}
                {step === "done" && "Payment Received"}
              </span>
              <button onClick={close} data-testid="cart-close"><X className="w-5 h-5 text-slate-300 hover:text-orange-500 transition-colors" /></button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {items.length === 0 ? (
                    <p className="text-slate-500 font-mono-plex text-sm tracking-wide mt-10 text-center">Your cart is empty.</p>
                  ) : (
                    <div className="flex flex-col gap-6">
                      {items.map((i) => (
                        <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                          <div className="w-20 h-20 bg-[#0A1118] overflow-hidden shrink-0">
                            {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between gap-2">
                              <h4 className="font-display text-sm uppercase leading-tight">{i.name}</h4>
                              <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.product_id}`}>
                                <Trash2 className="w-4 h-4 text-slate-500 hover:text-red-400 transition-colors" />
                              </button>
                            </div>
                            {i.color && <p className="text-slate-500 text-xs mt-0.5">{i.color}</p>}
                            {i.custom_specs && (
                              <div className="mt-1 space-y-0.5" data-testid="cart-item-specs">
                                {Object.entries(i.custom_specs).map(([k, v]) => (
                                  <p key={k} className="text-slate-500 text-[10px] leading-tight">
                                    <span className="text-orange-400/80">{k}:</span> {Array.isArray(v) ? v.join(", ") : v}
                                  </p>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center border border-white/10">
                                <button className="px-2 py-1" onClick={() => updateQty(i.key, i.quantity - 1)}><Minus className="w-3 h-3" /></button>
                                <span className="px-3 font-mono-plex text-xs">{i.quantity}</span>
                                <button className="px-2 py-1" onClick={() => updateQty(i.key, i.quantity + 1)}><Plus className="w-3 h-3" /></button>
                              </div>
                              <span className="font-mono-plex text-sm text-orange-500">${(i.price * i.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {items.length > 0 && (
                  <div className="px-6 py-6 border-t border-white/5">
                    <div className="flex justify-between mb-1">
                      <span className="font-mono-plex text-xs tracking-widest uppercase text-slate-400">Subtotal</span>
                      <span className="font-display text-xl text-orange-500">${total.toFixed(2)}</span>
                    </div>
                    {/* Free shipping progress nudge */}
                    <div className="mb-4" data-testid="free-shipping-progress">
                      {freeShip ? (
                        <p className="font-mono-plex text-[10px] tracking-wide text-green-400 mb-1.5">You&rsquo;ve unlocked FREE shipping</p>
                      ) : (
                        <p className="font-mono-plex text-[10px] tracking-wide text-slate-500 mb-1.5">
                          <span className="text-orange-400">${awayFromFree.toFixed(2)}</span> away from free shipping
                        </p>
                      )}
                      <div className="h-1 bg-white/10 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${freeShip ? "bg-green-400" : "bg-orange-500"}`}
                          style={{ width: `${Math.min(100, (merchandise / shipCfg.free_threshold) * 100)}%` }}
                          data-testid="free-shipping-bar"
                        />
                      </div>
                    </div>
                    <p className="font-mono-plex text-[10px] tracking-wide text-slate-500 mb-5">
                      {freeShip ? "Free shipping · pay by card or PayPal" : `+ $${shipCfg.flat_rate} flat-rate shipping · free over $${shipCfg.free_threshold}`}
                    </p>
                    <button
                      onClick={() => setStep("checkout")}
                      className="w-full bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 flex items-center justify-center gap-2 hover:bg-orange-600 active:scale-[0.98] transition-all"
                      data-testid="checkout-button"
                    >
                      Checkout <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}

            {step === "checkout" && (
              <form onSubmit={payWithCard} className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-5" data-testid="checkout-form">
                <p className="text-slate-400 text-sm leading-relaxed">
                  Enter your details, then pay securely by card or PayPal. Your order ships as soon as payment clears.
                </p>
                {[
                  { name: "customer_name", label: "Full Name", type: "text", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Phone", type: "tel", required: false },
                  { name: "address", label: "Shipping Address", type: "text", required: true },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">{f.label}</label>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-slate-100"
                      data-testid={`checkout-${f.name}`}
                    />
                  </div>
                ))}
                <div>
                  <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Note (optional)</label>
                  <textarea
                    rows={2}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-slate-100 resize-none"
                    data-testid="checkout-note"
                  />
                </div>
                <div>
                  <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Ambassador / Referral Code (optional)</label>
                  {appliedCode ? (
                    <div className="flex items-center justify-between mt-2 border border-[#AEB358]/40 bg-[#AEB358]/10 px-3 py-2">
                      <span className="font-mono-plex text-xs text-[#AEB358] tracking-widest uppercase">{appliedCode} · {discountPct}% off</span>
                      <button type="button" onClick={removeCode} className="text-slate-400 hover:text-red-400" data-testid="remove-code"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        placeholder="e.g. JAKE10"
                        className="flex-1 bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-slate-100 uppercase"
                        data-testid="checkout-code"
                      />
                      <button type="button" onClick={applyCode} className="px-4 border border-white/20 font-mono-plex text-[10px] tracking-widest uppercase hover:bg-white/5 transition-colors" data-testid="apply-code">Apply</button>
                    </div>
                  )}
                  {codeMsg && !appliedCode && <p className="text-slate-500 text-[11px] mt-1.5" data-testid="code-msg">{codeMsg}</p>}
                </div>
                <div className="pt-2 space-y-1.5">
                  <div className="flex justify-between">
                    <span className="font-mono-plex text-[11px] tracking-widest uppercase text-slate-500">Subtotal</span>
                    <span className="font-mono-plex text-sm text-slate-400">${total.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between">
                      <span className="font-mono-plex text-[11px] tracking-widest uppercase text-[#AEB358]">Discount ({discountPct}%)</span>
                      <span className="font-mono-plex text-sm text-[#AEB358]">−${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-mono-plex text-[11px] tracking-widest uppercase text-slate-500">
                      {freeShip ? "Shipping (free over $50)" : "Shipping (flat)"}
                    </span>
                    <span className={`font-mono-plex text-sm ${freeShip ? "text-green-400" : "text-slate-400"}`} data-testid="checkout-shipping">
                      {freeShip ? "FREE" : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="font-mono-plex text-xs tracking-widest uppercase text-slate-400">Total</span>
                    <span className="font-display text-xl text-orange-500" data-testid="checkout-total">${payable.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex gap-3 mt-2">
                  <button type="button" onClick={() => setStep("cart")} className="px-5 py-4 border border-white/20 font-mono-plex text-xs tracking-widest uppercase hover:bg-white/5 transition-colors" data-testid="checkout-back">Back</button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 hover:bg-orange-600 active:scale-[0.98] transition-all disabled:opacity-50"
                    data-testid="pay-card-button"
                  >
                    {submitting ? "Redirecting…" : "Pay with Card"}
                  </button>
                </div>
                {PAYPAL_CID && (
                  <div className="mt-6 pt-6 border-t border-white/10" data-testid="paypal-section">
                    <p className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-4 text-center">
                      — or pay with PayPal —
                    </p>
                    {form.customer_name && form.email && form.address ? (
                      <PayPalScriptProvider options={{ clientId: PAYPAL_CID, currency: "USD", intent: "capture" }}>
                        <PayPalButtons
                          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 45 }}
                          createOrder={createPaypalOrder}
                          onApprove={onPaypalApprove}
                          onError={() => toast.error("PayPal error — please try again")}
                        />
                      </PayPalScriptProvider>
                    ) : (
                      <p className="text-slate-500 text-xs text-center leading-relaxed">
                        Add your name, email and shipping address above to pay with PayPal.
                      </p>
                    )}
                  </div>
                )}
              </form>
            )}

            {step === "done" && (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-5" data-testid="order-success">
                <CheckCircle2 className="w-14 h-14 text-orange-500" />
                <h3 className="font-display text-3xl uppercase tracking-tight">Payment Received</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Thanks for your order! A receipt is on its way to your inbox, and we&rsquo;ll get it poured, packed and shipped.
                </p>
                <button onClick={close} className="mt-4 border border-white/20 px-8 py-4 font-mono-plex text-xs tracking-widest uppercase hover:bg-white/5 transition-colors" data-testid="continue-shopping">
                  Continue Browsing
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
